import moment from "moment";

import { getPropertyAddress } from "./verifier";

/**
 * Owner financial-status math.
 *
 * These formulas are intentionally identical to the historical page logic:
 * - rent is converted into owner currency and does not use paymentDirection
 * - expenses use paymentDirection (outgoing = negative)
 * - expense amounts are stored on the owner-currency key of { eur, ron }
 * - property subtotal is `rent - administrationFee + tax + heating + insurance + other`
 *   and is only shown in the column that matches rent.currency
 *
 * Invalid / incomplete payments are skipped (treated as 0) and reported in `warnings`
 * instead of throwing.
 */

export function toFiniteNumber(value) {
	const n = Number(value);
	return Number.isFinite(n) ? n : 0;
}

export function isFiniteAmount(value) {
	if (value === undefined || value === null || value === "") return false;
	return Number.isFinite(Number(value));
}

export function formatAmount(value) {
	return toFiniteNumber(value).toFixed(2);
}

export function emptyCurrencyPair() {
	return { eur: 0, ron: 0 };
}

export function resolvePropertyAddress(property) {
	try {
		if (!property?.location) return "-";
		return getPropertyAddress(property) || "-";
	} catch {
		return "-";
	}
}

/**
 * Compact label for wide tables: "Aviatiei 10/5". Full address stays available
 * for tooltips via `resolvePropertyAddress`.
 */
export function resolveShortPropertyLabel(property) {
	const location = property?.location;
	if (!location) return "-";
	const street = String(location.street || "").trim();
	const building = String(location.buildingNumber || "").trim();
	const apartment = String(location.apartmentNumber || "").trim();
	if (!street && !building && !apartment) return "-";
	const number = [building, apartment].filter(Boolean).join("/");
	return [street, number].filter(Boolean).join(" ");
}

/** Expense categories, in the order the table displays them. */
export const EXPENSE_CATEGORIES = [
	{ key: "administrationFee", label: "Administration fee" },
	{ key: "tax", label: "Tax" },
	{ key: "centralHeatingVerification", label: "Central Heating" },
	{ key: "insurance", label: "Insurance" },
	{ key: "other", label: "Others" },
];

function rowHasExpenseType(row, key) {
	return (row?.invoicesOnAddress || []).some((payment) => payment.type === key);
}

/**
 * Categories that actually have payments, even if they net to 0.
 * Lets the table drop columns with no activity without hiding cancelling amounts.
 */
export function getUsedExpenseCategoriesFromRows(rows = []) {
	return EXPENSE_CATEGORIES.filter(({ key }) => (rows || []).some((row) => rowHasExpenseType(row, key)));
}

export function getUsedExpenseCategories(months) {
	return getUsedExpenseCategoriesFromRows((months || []).flatMap((month) => month?.byProperty || []));
}

export function signedExpenseMultiplier(payment) {
	return payment?.paymentDirection === "outgoing" ? -1 : 1;
}

function pushWarning(warnings, warning) {
	if (warnings) warnings.push(warning);
}

/**
 * Convert a payment into owner currency.
 * Same-currency: use `amount`. Other currency: use `exchange.amount`.
 */
export function amountInOwnerCurrency(payment, ownerCurrency, warnings, context = {}) {
	if (!payment) {
		pushWarning(warnings, { code: "missing_payment", ...context });
		return { amount: 0, exchangeAmount: 0, exchangeCurrency: "", skipped: true };
	}

	if (payment.currency === ownerCurrency) {
		if (!isFiniteAmount(payment.amount)) {
			pushWarning(warnings, {
				code: "invalid_amount",
				paymentId: payment.id,
				paymentType: payment.type,
				currency: payment.currency,
				rawValue: payment.amount,
				...context,
			});
		}
		return {
			amount: toFiniteNumber(payment.amount),
			exchangeAmount: toFiniteNumber(payment.exchange?.amount),
			exchangeCurrency: payment.exchange?.currency || "",
			skipped: !isFiniteAmount(payment.amount),
		};
	}

	const hasExchangeAmount = payment.exchange?.amount !== undefined && payment.exchange?.amount !== null && payment.exchange?.amount !== "";
	if (!hasExchangeAmount) {
		pushWarning(warnings, {
			code: "missing_exchange",
			paymentId: payment.id,
			paymentType: payment.type,
			currency: payment.currency,
			...context,
		});
		return {
			amount: 0,
			exchangeAmount: toFiniteNumber(payment.amount),
			exchangeCurrency: payment.currency || "",
			skipped: true,
		};
	}

	if (!isFiniteAmount(payment.exchange.amount)) {
		pushWarning(warnings, {
			code: "invalid_amount",
			paymentId: payment.id,
			paymentType: payment.type,
			currency: payment.currency,
			rawValue: payment.exchange.amount,
			...context,
		});
		return {
			amount: 0,
			exchangeAmount: toFiniteNumber(payment.amount),
			exchangeCurrency: payment.currency || "",
			skipped: true,
		};
	}

	return {
		amount: toFiniteNumber(payment.exchange.amount),
		exchangeAmount: toFiniteNumber(payment.amount),
		exchangeCurrency: payment.currency || "",
		skipped: false,
	};
}

export function sumRent(payments, ownerCurrency, warnings, context = {}) {
	return (payments || [])
		.filter((payment) => payment.type === "rent")
		.reduce(
			(accumulator, payment) => {
				const converted = amountInOwnerCurrency(payment, ownerCurrency, warnings, context);
				return {
					...accumulator,
					description: [
						...accumulator.description,
						`${converted.amount} ${ownerCurrency} ${payment.paymentMethod}${
							converted.exchangeAmount ? " - " + converted.exchangeAmount + " " + converted.exchangeCurrency : ""
						} - ${payment.description}`,
					],
					amount: accumulator.amount + converted.amount,
					paymentMethod: [...accumulator.paymentMethod, payment.paymentMethod],
					currency: ownerCurrency,
				};
			},
			{ amount: 0, currency: ownerCurrency, paymentMethod: [], description: [] },
		);
}

export function sumExpenseType(payments, type, ownerCurrency, warnings, context = {}) {
	return (payments || [])
		.filter((payment) => payment.type === type)
		.reduce((accumulator, payment) => {
			const converted = amountInOwnerCurrency(payment, ownerCurrency, warnings, context);
			const signed = converted.amount * signedExpenseMultiplier(payment);
			return {
				...accumulator,
				[ownerCurrency]: toFiniteNumber(accumulator[ownerCurrency]) + signed,
			};
		}, emptyCurrencyPair());
}

export function getPropertySubtotal(row, currency) {
	if (!row || row.rent?.currency !== currency) return 0;
	return (
		toFiniteNumber(row.rent.amount) -
		toFiniteNumber(row.administrationFee?.[currency]) +
		toFiniteNumber(row.tax?.[currency]) +
		toFiniteNumber(row.centralHeatingVerification?.[currency]) +
		toFiniteNumber(row.insurance?.[currency]) +
		toFiniteNumber(row.other?.[currency])
	);
}

export function getMonthNet(totals) {
	const netFor = (currency) =>
		toFiniteNumber(totals?.rent?.[currency]) -
		toFiniteNumber(totals?.administrationFee?.[currency]) +
		toFiniteNumber(totals?.tax?.[currency]) +
		toFiniteNumber(totals?.centralHeatingVerification?.[currency]) +
		toFiniteNumber(totals?.insurance?.[currency]) +
		toFiniteNumber(totals?.other?.[currency]);

	return {
		eur: netFor("eur"),
		ron: netFor("ron"),
	};
}

export function isPaymentInMonth(payment, year, monthIndex) {
	if (!payment?.paymentDate) return false;
	const date = moment(payment.paymentDate);
	if (!date.isValid()) return false;
	return date.month() === monthIndex && date.year() === year;
}

export function buildPropertyMonthRow(property, monthPayments, ownerCurrency, warnings, context = {}) {
	const address = resolvePropertyAddress(property);
	const propertyId = property?.id;
	const invoices = (monthPayments || []).filter((payment) => payment.propertyId === propertyId);
	const ctx = { ...context, propertyId, address };

	const rent = sumRent(invoices, ownerCurrency, warnings, ctx);
	const otherPayments = invoices.filter((payment) => payment.type === "other");

	return {
		propertyId,
		address,
		shortAddress: resolveShortPropertyLabel(property),
		hasActivity: invoices.length > 0,
		invoicesOnAddress: invoices,
		rent: {
			amount: rent.amount || 0,
			currency: rent.currency || ownerCurrency || "",
			paymentMethod: rent.paymentMethod || [],
			description: rent.description || [],
		},
		administrationFee: sumExpenseType(invoices, "administrationFee", ownerCurrency, warnings, ctx),
		tax: sumExpenseType(invoices, "tax", ownerCurrency, warnings, ctx),
		insurance: sumExpenseType(invoices, "insurance", ownerCurrency, warnings, ctx),
		centralHeatingVerification: sumExpenseType(invoices, "centralHeatingVerification", ownerCurrency, warnings, ctx),
		other: sumExpenseType(invoices, "other", ownerCurrency, warnings, ctx),
		otherDescription: otherPayments.map(
			(payment) =>
				`${payment.paymentDirection === "incoming" ? "+" : "-"}${payment.amount} ${payment.currency} - ${payment.description || "Fara descriere"}`,
		),
	};
}

function withSubtotals(row) {
	return {
		...row,
		subtotal: {
			eur: getPropertySubtotal(row, "eur"),
			ron: getPropertySubtotal(row, "ron"),
		},
	};
}

export function flattenPropertyPayments(properties, warnings, { warnDates = false } = {}) {
	return (properties || []).flatMap((property) => {
		const address = resolvePropertyAddress(property);
		return (property.payments || []).map((payment) => {
			// No paymentDate means the money has not moved yet, so the payment is simply
			// not part of any month. Only a date that exists but cannot be parsed is a problem.
			if (warnDates && payment?.paymentDate && !moment(payment.paymentDate).isValid()) {
				pushWarning(warnings, {
					code: "invalid_payment_date",
					paymentId: payment?.id,
					paymentType: payment?.type,
					paymentDate: payment?.paymentDate,
					propertyId: property?.id,
					address,
				});
			}
			return {
				...payment,
				address,
				propertyId: property?.id,
			};
		});
	});
}

export function buildMonthBreakdown({ properties = [], year, monthIndex, ownerCurrency, payments } = {}) {
	const warnings = [];
	if (!ownerCurrency && !payments) {
		warnings.push({ code: "missing_owner_currency", year, monthIndex });
	}

	const allPayments = payments || flattenPropertyPayments(properties, warnings);
	const invoicesThisMonth = allPayments.filter((payment) => isPaymentInMonth(payment, year, monthIndex));

	const byProperty = (properties || []).map((property) =>
		withSubtotals(buildPropertyMonthRow(property, invoicesThisMonth, ownerCurrency, warnings, { year, monthIndex })),
	);

	const sumPair = (field) => ({
		eur: byProperty.reduce((sum, row) => sum + toFiniteNumber(row[field]?.eur), 0),
		ron: byProperty.reduce((sum, row) => sum + toFiniteNumber(row[field]?.ron), 0),
	});

	const totals = {
		rent: {
			eur: byProperty.filter((row) => row.rent.currency === "eur").reduce((sum, row) => sum + toFiniteNumber(row.rent.amount), 0),
			ron: byProperty.filter((row) => row.rent.currency === "ron").reduce((sum, row) => sum + toFiniteNumber(row.rent.amount), 0),
		},
		administrationFee: sumPair("administrationFee"),
		tax: sumPair("tax"),
		insurance: sumPair("insurance"),
		centralHeatingVerification: sumPair("centralHeatingVerification"),
		other: sumPair("other"),
	};
	totals.net = getMonthNet(totals);

	return {
		monthIndex,
		year,
		byProperty,
		totals,
		warnings,
	};
}

export function buildYearBreakdown({ properties = [], year, ownerCurrency, financialStatus }) {
	const yearWarnings = [];
	if (!ownerCurrency) {
		yearWarnings.push({ code: "missing_owner_currency", year });
	}
	const payments = flattenPropertyPayments(properties, yearWarnings, { warnDates: true });

	const months = Array.from({ length: 12 }, (_, monthIndex) => {
		const month = buildMonthBreakdown({ properties, year, monthIndex, ownerCurrency, payments });
		const saved = getSavedMonthEntry(financialStatus, year, monthIndex);
		if (saved?.amount && saved?.currency) {
			const live = toFiniteNumber(month.totals.net?.[saved.currency]);
			const savedAmount = toFiniteNumber(saved.amount);
			if (Math.abs(live - savedAmount) > 0.009) {
				month.warnings.push({
					code: "finalized_differs",
					year,
					monthIndex,
					savedAmount,
					liveAmount: live,
					currency: saved.currency,
				});
			}
		}
		return month;
	});

	return {
		year,
		months,
		warnings: [...yearWarnings, ...months.flatMap((month) => month.warnings)],
	};
}

export function getSavedMonthEntry(financialStatus, year, monthIndex) {
	return financialStatus?.[year]?.[monthIndex] || financialStatus?.[year]?.[String(monthIndex)] || null;
}

export function getTotalAmountByStatus(financialStatus, status) {
	if (!financialStatus || typeof financialStatus !== "object") return 0;
	let totalAmount = 0;
	Object.keys(financialStatus).forEach((yearKey) => {
		const yearEntry = financialStatus[yearKey];
		if (!yearEntry || typeof yearEntry !== "object") return;
		Object.keys(yearEntry).forEach((monthKey) => {
			const monthEntry = yearEntry[monthKey];
			if (monthEntry?.status === status) {
				totalAmount += toFiniteNumber(monthEntry.amount);
			}
		});
	});
	return totalAmount;
}

export function getRentColorScheme(rentAmount, savedEntry) {
	if (savedEntry?.status) return "green";
	if (rentAmount === 0) return "red";
	return "yellow";
}

export function getDisplayedMonthNet(savedEntry, liveNet, currency) {
	const live = toFiniteNumber(liveNet?.[currency]);
	const useSaved = Boolean(savedEntry?.amount && savedEntry?.currency === currency);
	return {
		primary: useSaved ? toFiniteNumber(savedEntry.amount) : live,
		live,
		useSaved,
	};
}

export function getFinalizeMonthDefaults({ financialStatus, year, monthIndex, ownerCurrency, liveNet, ownerEmail, defaultDate }) {
	const saved = getSavedMonthEntry(financialStatus, year, monthIndex);
	const amount = saved?.amount
		? saved.amount
		: formatAmount(ownerCurrency === "ron" ? liveNet?.ron : liveNet?.eur);
	return {
		year,
		month: monthIndex,
		ownerEmail,
		currency: saved?.currency || ownerCurrency || "",
		amount,
		description: saved?.description || "",
		status: saved?.status || "",
		date: saved?.date || defaultDate,
		document: saved?.document || {},
	};
}

export function formatMonthStatus(savedEntry, dateFormat) {
	if (!savedEntry?.status) return "-";
	const date = savedEntry.date ? moment(savedEntry.date) : null;
	if (date?.isValid()) return `${savedEntry.status} - ${date.format(dateFormat)}`;
	return String(savedEntry.status);
}

export function getActiveAdministrationContracts(properties) {
	const contracts = (properties || [])
		.map((property) => {
			const document = (property.documents || []).find((item) => item.type === "administrationContract" && item.active === true);
			if (!document) return null;
			return { ...document, address: resolvePropertyAddress(property) };
		})
		.filter(Boolean);

	const seen = {};
	return contracts.filter((item) => {
		const value = item.number;
		if (seen[value]) return false;
		seen[value] = true;
		return true;
	});
}
